import { OraclePriceEntry, PriceDerivation } from './types'

/**
 * Rank order of `PriceDerivation` — lower is better. Used directly as a
 * ranking tier in `selectAssetGroupPrices`.
 */
export const DERIVATION_RANK: Record<PriceDerivation, number> = {
  direct: 0,
  quoted: 1,
  derived: 2,
}

/**
 * Resolve an entry's derivation class.
 *
 * Precedence:
 * 1. What the fetcher put on the entry (most specific — e.g. Euler knows
 *    per-vault whether its unit of account is a fiat code or a token).
 * 2. The fetcher group's declared default. `derived` wins over the
 *    structural guess below: Fluid/Silo collateral legs carry a `baseAsset`
 *    exactly like a real quoted feed, but there is no feed for the asset —
 *    only a market ratio.
 * 3. The `baseAsset`/`staticBase` convention every fetcher already follows:
 *    a non-base asset carrying a `baseAsset` is quoted in it; anything else
 *    is a direct USD read.
 *
 * Step 3 is what keeps third-party or older entries (which predate the
 * `derivation` field) from silently ranking as `direct`.
 */
export function resolveDerivation(
  entry: OraclePriceEntry,
  groupDefault?: PriceDerivation,
): PriceDerivation {
  if (entry.derivation) return entry.derivation
  if (groupDefault === 'derived') return 'derived'
  if (entry.baseAsset && !entry.staticBase) return 'quoted'
  return groupDefault ?? 'direct'
}
